import { API_URL, getAuthEndpoint } from './config';

// Debug logging
console.log('🔧 Auth Provider Config:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    apiUrl: API_URL
});

export const decodeJWT = (token) => {
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

export const isTokenExpired = (token) => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;
    // exp is in seconds, Date.now() is ms
    return Date.now() >= decoded.exp * 1000;
};

export const refreshAccessToken = async (refreshToken, logoutCallback) => {
    if (!refreshToken) {
        console.log('❌ No refresh token available');
        if (logoutCallback) logoutCallback();
        return null;
    }

    try {
        const response = await fetch(getAuthEndpoint('refresh'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) {
            console.log('❌ Refresh token expired or invalid');
            if (logoutCallback) logoutCallback();
            return null;
        }

        const data = await response.json();
        const { token, refresh_token, user } = data;

        // Update localStorage
        const authData = { token, refresh_token, user };
        localStorage.setItem('auth', JSON.stringify(authData));
        localStorage.setItem('token', token);

        console.log('✅ Token refreshed successfully');
        return token;
    } catch (error) {
        console.error('❌ Error refreshing token:', error);
        if (logoutCallback) logoutCallback();
        return null;
    }
};

export const authProvider = {
    login: async ({ username, password }) => {
        console.log(`🌐 Auth API Call: ${getAuthEndpoint('login')}`);
        const response = await fetch(getAuthEndpoint('login'), {
            method: 'POST',
            body: JSON.stringify({ email: username, password }),
            headers: new Headers({ 'Content-Type': 'application/json' }),
        });

        if (response.status < 200 || response.status >= 300) {
            const errorData = await response.json();
            throw new Error(errorData.error || errorData.message || 'Login failed');
        }

        const { token, refresh_token, user } = await response.json();
        
        // Check if user is admin
        if (user.role !== 'admin') {
            throw new Error('Access denied: Admin privileges required');
        }
        
        const authData = { token, refresh_token, user };
        localStorage.setItem('auth', JSON.stringify(authData));
        localStorage.setItem('token', token);

        return Promise.resolve();
    },

    logout: () => {
        localStorage.removeItem('auth');
        localStorage.removeItem('token');
        return Promise.resolve();
    },

    checkAuth: async () => {
        const auth = localStorage.getItem('auth');
        if (!auth) {
            return Promise.reject();
        }
        const { token, refresh_token, user } = JSON.parse(auth);
        if (user.role !== 'admin') {
            return Promise.reject('Access denied: Admin only');
        }

        if (token && user) {
            // Check if token is expired
            if (isTokenExpired(token)) {
                // Try to refresh
                const newToken = await refreshAccessToken(refresh_token, authProvider.logout);
                if (!newToken) {
                    authProvider.logout();
                    return Promise.reject('Token refresh failed');
                }
            } else {
                const authData = {
                    token,
                    refresh_token,
                    user
                }

                localStorage.setItem('auth', JSON.stringify(authData));
                localStorage.setItem('token', token);
            }
        }

        return Promise.resolve();
    },

    checkError: (error) => {
        const status = error.status;
        if (status === 401 || status === 403) {
            localStorage.removeItem('auth');
            localStorage.removeItem('token');
            return Promise.reject();
        }
        return Promise.resolve();
    },

    getPermissions: () => {
        const auth = localStorage.getItem('auth');
        if (!auth) {
            return Promise.reject();
        }
        const { user } = JSON.parse(auth);
        return Promise.resolve(user.role);
    },

    getIdentity: () => {
        const auth = localStorage.getItem('auth');
        if (!auth) {
            return Promise.reject();
        }
        const { user } = JSON.parse(auth);
        return Promise.resolve({
            id: user.id,
            fullName: user.name,
            avatar: null,
        });
    },
}; 